import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Container,
  MenuItem,
  Select,
  Tab,
  Typography,
} from "@mui/material";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import DmsParts from "../../compenents/Integration/ReInitiateDmsSetup/DmsParts";
import { CustomStepper } from "../../compenents/Integration/ReInitiateDmsSetup/CustomStepper";
import {
  useGetDealerLocationsMutation,
  setSelectManufacturer,
  addProductTypeList,
  useGetDMSManufacturersBySettingsIdQuery,
} from "../../redux/reducer/features/Integration/DmsSetupSlice";
import { AppState } from "../../redux/reducer/rootReducer";
import NonSyncedDmsProducts from "../../compenents/Integration/ReInitiateDmsSetup/NonSyncedDmsProducts";
import ShopifyProductGrid from "../../compenents/Integration/Synchronization/Grids/ShopifyProductGrid";
import BigCommerceProductGrid from "../../compenents/Integration/Synchronization/Grids/BigCommerceProductGrid";
import { setHasUnsavedChanges } from "../../redux/reducer/features/App/AppGridSlice";
import LinkInventoryReview from "../../compenents/Integration/Synchronization/Grids/LinkInventoryReview";
import { LinkInventoryDto } from "../../models/features/Integration/shopify";
import {
  IsShopifySetup,
  setupTypeLabelMap,
} from "../../services/utilities/utility";
import {
  PlatformFeature,
  SetupType,
} from "../../models/features/Integration/DmsSetup";
import { hasPlatformFeatureAccess } from "../../services/featurePermissions/featureMatrix";
import {
  SubscriptionFeatureName,
  useHasSubscriptionFeature,
} from "../../services/featurePermissions/subscriptionFeatures";
import ReInitiateShopify from "../../compenents/Integration/ReInitiateDmsSetup/ReInitiateShopify";
import WordpressProductGrid from "../../compenents/Integration/Synchronization/Grids/WordpressProductGrid";
const ReInitiateDmsSetup: React.FunctionComponent = () => {
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);
  const setupType = dmsSetup?.setupType;

  const dispatch = useDispatch();
  const hasWholegoods = useHasSubscriptionFeature(
    SubscriptionFeatureName.Wholegoods,
  );
  const hasParts = useHasSubscriptionFeature(SubscriptionFeatureName.Parts);
  const [productType, setProductType] = React.useState(0);
  const [value, setValue] = React.useState(
    hasWholegoods ? "Wholegoods" : hasParts ? "Parts" : "LinkInventory",
  );
  const [selectedProducts, setSelectedProducts] = React.useState<
    LinkInventoryDto[]
  >([]);
  const [activeSteps, setActiveSteps] = React.useState<{
    [key: string]: number;
  }>({
    Wholegoods: 0,
    Parts: 0,
    LinkInventory: 0,
  });
  const [skippedSteps, setSkippedSteps] = React.useState<{
    [key: string]: Set<number>;
  }>({
    Wholegoods: new Set(),
    Parts: new Set(),
  });

  const everestDmsDealerId = useSelector(
    (state: AppState) => state.authReducer.everestDmsDealerId,
  );
  const partsPortalSetting = useSelector(
    (state: AppState) => state.shoppingPortalReducer,
  );

  const { dmsWholeGoods, dmsParts } = useSelector(
    (state: AppState) => state.dmsReducer,
  );

  const showLinkInventoryTab =
    useHasSubscriptionFeature(SubscriptionFeatureName.LinkInventory) &&
    hasPlatformFeatureAccess(
      dmsSetup?.setupType,
      PlatformFeature.LinkInventory,
    );

  const [dmsLocationMutation, dmsLocationMutationResult] =
    useGetDealerLocationsMutation();

  const { data, refetch, isSuccess, isLoading } =
    useGetDMSManufacturersBySettingsIdQuery({
      dealerSettingsId: dmsSetup?.dealerSettings?.id ?? "",
    });

  React.useEffect(() => {
    dmsLocationMutation({
      everestDmsDealerId: everestDmsDealerId ?? "",
    });
  }, []);

  React.useEffect(() => {
    if (value === "Wholegoods") {
      setProductType(0);
      dispatch(addProductTypeList(["Wholegoods"]));
    } else if (value === "Parts") {
      setProductType(1);
      dispatch(addProductTypeList(["Parts"]));
    }
    handleReset();
  }, [value]);

  React.useEffect(() => {
    if (!isLoading && data) {
      dispatch(
        setSelectManufacturer({ dmsManufacturers: data.entityList ?? [] }),
      );
    }
  }, [isLoading, data]);

  React.useEffect(() => {
    dispatch(
      setHasUnsavedChanges(
        dmsWholeGoods.length > 0 || (dmsParts?.length ?? 0) > 0,
      ),
    );
  }, [dmsWholeGoods, dmsParts]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const currentStep = activeSteps[value];
  const currentSkipped = skippedSteps[value];

  const isStepSkipped = (step: number) => {
    return currentSkipped.has(step);
  };

  const handleStep = (step: number) => () => {
    setActiveSteps((prev) => ({ ...prev, [value]: step }));
  };

  const handleNext = () => {
    let newSkipped = currentSkipped;
    if (isStepSkipped(currentStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(currentStep);
    }
    setActiveSteps((prev) => ({ ...prev, [value]: currentStep + 1 }));
    setSkippedSteps((prev) => ({ ...prev, [value]: newSkipped }));
  };

  const handleBack = () => {
    setActiveSteps((prev) => ({ ...prev, [value]: currentStep - 1 }));
  };

  const handleReset = () => {
    setActiveSteps((prev) => ({ ...prev, [value]: 0 }));
    setSkippedSteps((prev) => ({ ...prev, [value]: new Set() }));
  };

  const wholegoodSteps = [
    {
      label: "Select Wholegoods",
      component: (
        <NonSyncedDmsProducts handleStep={handleStep} handleNext={handleNext} />
      ),
      isRequired: true,
    },
    {
      label: "Review",
      component: (
        <ReInitiateShopify
          handleBack={handleBack}
          locations={dmsLocationMutationResult.data?.entityList ?? []}
          productType={productType}
          setTabValue={setValue}
        />
      ),
      isRequired: true,
    },
  ];

  const partSteps = [
    {
      label: "Select Parts",
      component: <DmsParts handleNext={handleNext} />,
      isRequired: true,
    },
    {
      label: "Review",
      component: (
        <ReInitiateShopify
          handleBack={handleBack}
          locations={dmsLocationMutationResult.data?.entityList ?? []}
          productType={productType}
          setTabValue={setValue}
        />
      ),
      isRequired: true,
    },
  ];
  const linkInventorySteps = [
    {
      label: `Select ${
        setupType && setupType in setupTypeLabelMap
          ? setupTypeLabelMap[setupType]
          : "Store"
      } Products`,
      component: IsShopifySetup(setupType) ? (
        <ShopifyProductGrid
          handleNext={handleNext}
          setSelectedProducts={setSelectedProducts}
        />
      ) : setupType === SetupType.BigCommerce ? (
        <BigCommerceProductGrid
          handleNext={handleNext}
          setSelectedProducts={setSelectedProducts}
        />
      ) : (
        <WordpressProductGrid
          handleNext={handleNext}
          setSelectedProducts={setSelectedProducts}
        />
      ),
      isRequired: true,
    },
    {
      label: "Review",
      component: (
        <LinkInventoryReview
          handleBack={handleBack}
          StoreProducts={selectedProducts}
        />
      ),
      isRequired: true,
    },
  ];
  return (
    <Container maxWidth={false}>
      <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
        {value !== "LinkInventory"
          ? value
          : showLinkInventoryTab
            ? "Link Inventory"
            : "Sync"}
      </Typography>
      {!hasWholegoods && !hasParts && !showLinkInventoryTab && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your subscription tier does not include access to any product type.
          Please contact your administrator to upgrade your subscription.
        </Alert>
      )}
      <Box sx={{ backgroundColor: "white" }}>
        <TabContext value={value}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <TabList
              onChange={handleChange}
              className="custom-tab-list"
              sx={{
                "& .MuiButtonBase-root": {
                  maxWidth: "100px",
                },
              }}
            >
              {hasWholegoods && (
                <Tab label="DMS Wholegoods" value="Wholegoods" />
              )}
              {partsPortalSetting &&
                partsPortalSetting.isPortalEnabled === false &&
                hasParts && <Tab label="DMS Parts" value="Parts" />}
              {showLinkInventoryTab && (
                <Tab label="Link Inventory" value="LinkInventory" />
              )}
            </TabList>
          </Box>
          {hasWholegoods && (
            <TabPanel value="Wholegoods" className="tabPanelPadding">
              <CustomStepper
                steps={wholegoodSteps}
                activeStep={activeSteps["Wholegoods"]}
                isStepSkipped={isStepSkipped}
                handleReset={handleReset}
              />
            </TabPanel>
          )}
          {partsPortalSetting &&
            partsPortalSetting.isPortalEnabled === false &&
            hasParts && (
              <TabPanel value="Parts" className="tabPanelPadding">
                <CustomStepper
                  steps={partSteps}
                  activeStep={activeSteps["Parts"]}
                  isStepSkipped={isStepSkipped}
                  handleReset={handleReset}
                />
              </TabPanel>
            )}
          {showLinkInventoryTab && (
            <TabPanel value="LinkInventory" className="tabPanelPadding">
              <CustomStepper
                steps={linkInventorySteps}
                activeStep={activeSteps["LinkInventory"]}
                isStepSkipped={(step: number) => false}
                handleReset={handleReset}
              />
            </TabPanel>
          )}
        </TabContext>
      </Box>
    </Container>
  );
};

export default ReInitiateDmsSetup;
